import type {ComponentProps} from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import {cn} from "../lib/utils";

export type SeparatorOrientation = "horizontal" | "vertical";

// A rule is a rule — the palette tokens a hairline reads as. The rest of the palette lives in
// `PaletteColor` (lib/color.ts) for props that paint a surface; reach for `className` for a one-off.
export type SeparatorColor = "foreground-400" | "foreground-300" | "foreground-100" | "brand-main" | "tone-red";

export type SeparatorProps = ComponentProps<typeof SeparatorPrimitive.Root> & {color?: SeparatorColor};

export const Separator = ({
	orientation = "horizontal", decorative = true, color = "foreground-400", className, ...props
}: SeparatorProps) =>
	<SeparatorPrimitive.Root
		data-slot="separator"
		orientation={orientation}
		decorative={decorative}
		className={cn("shrink-0", colorStyles[color], orientationStyles[orientation], className)}
		{...props}/>;

Separator.displayName = "Separator";

const orientationStyles: Record<SeparatorOrientation, string> = {
	horizontal: "h-px w-full",
	vertical: "w-px self-stretch"
};

// Written out rather than built from the token, so JIT finds these in source and the subset needs
// no safelist entry — unlike Stack's `backgroundColor`, which spans the whole palette.
const colorStyles: Record<SeparatorColor, string> = {
	"foreground-400": "bg-foreground-400",
	"foreground-300": "bg-foreground-300",
	"foreground-100": "bg-foreground-100",
	"brand-main": "bg-brand-main",
	"tone-red": "bg-tone-red"
};
